import { ApplicationConfig, importProvidersFrom, inject } from '@angular/core';
import {
	provideRouter,
	withPreloading,
	Router,
	UrlSerializer,
	TitleStrategy,
	withComponentInputBinding,
	withRouterConfig
} from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
	provideHttpClient,
	withInterceptorsFromDi,
	HTTP_INTERCEPTORS
} from '@angular/common/http';
import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideToastr } from 'ngx-toastr';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';

// Import routes and services
import { reducers } from './store';
import { UserEffects } from './store/effects/user.effects';
import { ReviewEntriesEffects } from '@pages/review-entries/store/effects/review-entries.effects';
import { HomeEffects } from '@pages/home/store/effects/home.effects';
import { RolePermissionEffects } from './store/role-permission/effects/role-permission.effects';

// Import guards
import { AuthGuard } from '@core/guards/auth.guard';
import { IsRmsUserGuard } from '@core/guards/is-rms-user.guard';
import { RcGuard } from '@core/guards/rc.guard';

// Import interceptors
import { JwtInterceptor } from '@core/interceptors/jwt.interceptor';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { LoaderInterceptor } from '@core/interceptors/loader.interceptor';
import { SecurityInterceptor } from '@core/interceptors/security.interceptor';
import { BaseUrlInterceptor } from '@core/interceptors/base-url.interceptor';

// Import custom components and services
import { CustomToastComponent } from '@shared/custom-toast/custom-toast.component';
import {
	routes,
	SelectivePreloadingStrategy,
	CustomUrlSerializer
} from '@app/app.routes';

// Import performance optimization services
import { DynamicLibraryLoaderService } from '@core/services/dynamic-library-loader.service';
import { PerformanceMonitoringService } from '@core/services/performance-monitoring.service';
import { OptimizedImageService } from '@core/services/optimized-image.service';
import { CustomTitleStrategy } from '@core/services/custom-title-strategy.service';

// Helper functions from the original module
export function getBaseHref(platformLocation: PlatformLocation): string {
	return platformLocation.getBaseHrefFromDOM();
}

async function fetchDynamicPrefix(): Promise<string> {
	let currentTenant = localStorage.getItem('tenant');
	if (currentTenant !== null) {
		let tenant = JSON.parse(currentTenant);
		return tenant?.Name;
	} else {
		return 'kiosk';
	}
}

export function initializeApp(): () => Promise<void> {
	return async () => {
		const router = inject(Router);
		// Logic to determine the dynamic prefix (e.g., based on language)
		const dynamicPrefix = await fetchDynamicPrefix();

		// Get the current URL
		const currentUrl = router.url;

		// Check if the current URL is the language selection page
		const isLanguageSelectionPage = currentUrl === '/auth/select-rc';

		// Get the base element
		const baseElement = document.querySelector('base');

		// Ensure the base element exists before manipulating it
		if (baseElement && !isLanguageSelectionPage) {
			baseElement.setAttribute('href', dynamicPrefix);
		}
	};
}

export const appConfig: ApplicationConfig = {
	providers: [
		// Router configuration
		provideRouter(
			routes,
			withPreloading(SelectivePreloadingStrategy),
			withComponentInputBinding(),
			withRouterConfig({
				onSameUrlNavigation: 'reload',
				urlUpdateStrategy: 'deferred',
				paramsInheritanceStrategy: 'always'
			})
		),

		// HTTP Client
		provideHttpClient(withInterceptorsFromDi()),

		// Animations (Required for ngx-toastr and Material components)
		provideAnimations(),

		// Angular Material Dialog configuration
		{
			provide: MAT_DIALOG_DEFAULT_OPTIONS,
			useValue: {
				hasBackdrop: true,
				disableClose: false,
				autoFocus: true,
				restoreFocus: true,
				role: 'dialog',
				ariaModal: true,
				panelClass: 'custom-dialog-container'
			}
		},

		// NgRx Store - Optimized for production
		provideStore(reducers),
		provideEffects([
			UserEffects,
			ReviewEntriesEffects,
			HomeEffects,
			RolePermissionEffects
		]),
		provideStoreDevtools({
			maxAge: 10, // Reduced from 25 to save memory
			logOnly: true,
			connectInZone: true // Better performance
		}),

		// Toastr - Optimized for performance
		provideToastr({
			timeOut: 5000, // Reduced timeout
			toastComponent: CustomToastComponent,
			positionClass: 'toast-bottom-left',
			preventDuplicates: true,
			maxOpened: 1,
			progressBar: false, // Disabled for better performance
			closeButton: true,
			disableTimeOut: false,
			tapToDismiss: true,
			enableHtml: false, // Disabled for security and performance
			newestOnTop: true
		}),

		// Import additional modules that need to be configured
		importProvidersFrom(
			NgxSkeletonLoaderModule.forRoot({
				animation: 'pulse',
				loadingText: 'This item is actually loading...'
			})
		),

		// Guards
		AuthGuard,
		IsRmsUserGuard,
		RcGuard,

		// Base href provider
		{
			provide: APP_BASE_HREF,
			useFactory: getBaseHref,
			deps: [PlatformLocation]
		},

		// Custom URL Serializer
		{
			provide: UrlSerializer,
			useClass: CustomUrlSerializer
		},

		// Custom Title Strategy
		{
			provide: TitleStrategy,
			useClass: CustomTitleStrategy
		},

		// Preloading strategy
		SelectivePreloadingStrategy,

		// Performance optimization services
		DynamicLibraryLoaderService,
		PerformanceMonitoringService,
		OptimizedImageService,

		// HTTP Interceptors
		{
			provide: HTTP_INTERCEPTORS,
			useClass: BaseUrlInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: SecurityInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: JwtInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: LoaderInterceptor,
			multi: true
		}

		// Note: APP_INITIALIZER was commented out in the original module
		// If you need it, uncomment and add:
		// {
		//   provide: APP_INITIALIZER,
		//   useFactory: initializeApp,
		//   multi: true
		// }
	]
};
